import axios from 'axios';
import config from './config';
import { getEffectiveOrganizationId, getOrganizationContext, getOrganizationHeaders } from './orgContext';

export interface CourseSummary {
  id: number;
  name: string;
  inforPagenav?: string | null;
  parentCourseId?: number | null;
  parentCourseName?: string | null;
  description: string;
  difficultyLevel: string;
  prerequisites: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  sortOrder?: number;
}

type CachedCoursesPayload = {
  expiresAt: number;
  data: CourseSummary[];
};

const getCoursesCacheKey = (): string => {
  const effectiveOrganizationId = getEffectiveOrganizationId();
  if (effectiveOrganizationId) {
    return `courses-list-cache-v1-org-${effectiveOrganizationId}`;
  }

  const context = getOrganizationContext();
  return context ? `courses-list-cache-v1-org-${context.organizationId}` : 'courses-list-cache-v1';
};
const COURSES_CACHE_TTL_MS = 1000 * 60 * 30;
const inFlightCoursesRequests = new Map<string, Promise<CourseSummary[]>>();

const readCachedCourses = (): CourseSummary[] | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  const raw = sessionStorage.getItem(getCoursesCacheKey());
  if (!raw) {
    return null;
  }

  try {
    const parsed = JSON.parse(raw) as CachedCoursesPayload;
    if (!parsed || !Array.isArray(parsed.data) || typeof parsed.expiresAt !== 'number') {
      sessionStorage.removeItem(getCoursesCacheKey());
      return null;
    }

    if (Date.now() >= parsed.expiresAt) {
      sessionStorage.removeItem(getCoursesCacheKey());
      return null;
    }

    return parsed.data;
  } catch {
    sessionStorage.removeItem(getCoursesCacheKey());
    return null;
  }
};

const writeCachedCourses = (courses: CourseSummary[]) => {
  if (typeof window === 'undefined') {
    return;
  }

  const payload: CachedCoursesPayload = {
    expiresAt: Date.now() + COURSES_CACHE_TTL_MS,
    data: courses,
  };

  sessionStorage.setItem(getCoursesCacheKey(), JSON.stringify(payload));
};

export const clearCoursesCache = () => {
  if (typeof window === 'undefined') {
    return;
  }

  sessionStorage.removeItem(getCoursesCacheKey());
};

export const fetchCoursesCached = async (): Promise<CourseSummary[]> => {
  const cacheKey = getCoursesCacheKey();
  const cached = readCachedCourses();
  if (cached) {
    return cached;
  }

  const inFlightRequest = inFlightCoursesRequests.get(cacheKey);
  if (inFlightRequest) {
    return inFlightRequest;
  }

  const request = (async () => {
    const apiBaseUrl = `${config.apiBaseUrl}/api`;
    const response = await axios.get<CourseSummary[]>(`${apiBaseUrl}/courses`, {
      headers: {
        ...getOrganizationHeaders(),
      },
    });
    const courses = Array.isArray(response.data) ? response.data : [];
    writeCachedCourses(courses);
    return courses;
  })();

  inFlightCoursesRequests.set(cacheKey, request);

  try {
    return await request;
  } finally {
    inFlightCoursesRequests.delete(cacheKey);
  }
};